import type { ProblemDetails } from "./types";

/** Header and cookie of the double-submit CSRF scheme: the server sets a readable `XSRF-TOKEN` cookie, unsafe requests echo it. */
export const XSRF_COOKIE = "XSRF-TOKEN";
export const XSRF_HEADER = "X-XSRF-TOKEN";
export const SESSION_EXPIRED_EVENT = "pmo:session-expired";

const UNSAFE = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${encodeURIComponent(name)}=`;
  for (const part of document.cookie.split(";")) {
    const c = part.trim();
    if (c.startsWith(prefix)) return decodeURIComponent(c.slice(prefix.length));
  }
  return null;
}

const arabic = () => typeof document !== "undefined" && document.documentElement.lang.startsWith("ar");

/** The reader's side of an English / Arabic pair, falling back to the other side and then to `plain`. */
function pick<T>(en: T | undefined, ar: T | undefined, plain: T | undefined): T | undefined {
  return (arabic() ? ar ?? en : en ?? ar) ?? plain;
}

function first(map: Record<string, string[]>, field: string): string | undefined {
  const key = Object.keys(map).find((k) => k.toLowerCase() === field.toLowerCase());
  return key ? map[key]?.[0] : undefined;
}

/**
 * An API failure. `status` is 0 when the service could not be reached at all (network error, proxy with no backend); problem
 * documents (RFC 7807, `application/problem+json`) fill `code`, `title`, `detail`, field `errors` and gate `blockers`. Text members
 * follow the current UI language, so a message already on screen switches with it.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly blockers: unknown[] | undefined;
  readonly traceId: string | undefined;
  readonly problem: ProblemDetails | undefined;

  constructor(status: number, problem?: ProblemDetails) {
    super();
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
    this.code = problem?.code;
    this.blockers = problem?.blockers;
    this.traceId = problem?.traceId;
    Object.defineProperty(this, "message", { get: () => this.detail ?? this.title, configurable: true });
  }

  get title(): string {
    const p = this.problem;
    const fallback = this.status === 0 ? pick("Service unavailable", "الخدمة غير متاحة", undefined) : pick(`Request failed (${this.status})`, `تعذّر إكمال الطلب (${this.status})`, undefined);
    return pick(p?.titleEn, p?.titleAr, p?.title) ?? fallback!;
  }

  get detail(): string | undefined {
    return pick(this.problem?.detailEn, this.problem?.detailAr, this.problem?.detail);
  }

  /** Field messages, keyed by field. */
  get errors(): Record<string, string[]> {
    return pick(this.problem?.errorsEn, this.problem?.errorsAr, this.problem?.errors) ?? {};
  }

  /** Field error codes (`required`, `format`…); servers before the bilingual messages sent the codes in `errors`. */
  get codes(): Record<string, string[]> {
    return this.problem?.errorCodes ?? this.problem?.errors ?? {};
  }

  /** The service is down or unreachable (as opposed to answering with an error). */
  get isUnavailable(): boolean {
    return this.status === 0 || this.status === 502 || this.status === 503 || this.status === 504 || (this.status === 500 && !this.problem);
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isValidation(): boolean {
    return this.status === 400 || this.status === 422;
  }

  /** First message for a field (keys are matched case-insensitively, as ASP.NET may send `Title` or `title`). */
  fieldError(field: string): string | undefined {
    return first(this.errors, field);
  }

  /** First error code for a field. */
  fieldCode(field: string): string | undefined {
    return first(this.codes, field);
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** Parses an error response into an {@link ApiError}, reading problem+json (or plain JSON) bodies when present. */
export async function toApiError(res: Response): Promise<ApiError> {
  const ct = res.headers.get("content-type") ?? "";
  let problem: ProblemDetails | undefined;
  if (ct.includes("json")) {
    try {
      problem = (await res.json()) as ProblemDetails;
    } catch {
      problem = undefined;
    }
  }
  return new ApiError(res.status, problem);
}

/** Fires the session-expired signal the shells listen for (a dialog asks the user to sign in again). */
export function emitSessionExpired(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

export function onSessionExpired(handler: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  window.addEventListener(SESSION_EXPIRED_EVENT, handler);
  return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
}

/** Paths whose 401 is an expected answer (not signed in yet), not an expired session. */
const AUTH_PROBES = ["/api/auth/session", "/api/auth/dev-login", "/api/auth/personas", "/api/auth/step-up"];

export function applyRequestHeaders(method: string, headers: Headers): void {
  if (!headers.has("Accept")) headers.set("Accept", "application/json, application/problem+json");
  if (typeof document !== "undefined" && document.documentElement.lang && !headers.has("Accept-Language")) headers.set("Accept-Language", document.documentElement.lang);
  if (UNSAFE.has(method.toUpperCase())) {
    const token = readCookie(XSRF_COOKIE);
    if (token) headers.set(XSRF_HEADER, token);
  }
}

export function handleUnauthorized(url: string, status: number): void {
  if (status !== 401) return;
  const path = url.startsWith("http") ? new URL(url).pathname : url.split("?")[0];
  if (AUTH_PROBES.some((p) => path?.startsWith(p))) return;
  emitSessionExpired();
}

export type QueryParams = Record<string, string | number | boolean | null | undefined | Array<string | number>>;

export function buildQuery(params?: QueryParams): string {
  if (!params) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) v.forEach((item) => sp.append(k, String(item)));
    else sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export interface RequestOptions extends Omit<RequestInit, "body" | "method"> {
  query?: QueryParams;
}

/** Base URL of the API. Empty = same origin (Vite proxies `/api` and `/hubs` to the backend in development). */
let baseUrl = "";
export function configureApi(options: { baseUrl?: string }): void {
  baseUrl = options.baseUrl ?? "";
}
export function apiBaseUrl(): string {
  return baseUrl;
}

async function request<T>(method: string, path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
  const { query, headers: extra, ...init } = options;
  const url = `${baseUrl}${path}${buildQuery(query)}`;
  const headers = new Headers(extra);
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;
  if (body !== undefined && !isForm && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  applyRequestHeaders(method, headers);
  let res: Response;
  try {
    res = await fetch(url, {
      ...init,
      method,
      headers,
      credentials: "include",
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiError(0);
  }
  if (!res.ok) {
    const error = await toApiError(res);
    // A step-up challenge (401 `step_up_required`) is answered in place by re-authenticating; the session itself is still valid.
    if (error.code !== "step_up_required") handleUnauthorized(path, res.status);
    throw error;
  }
  if (res.status === 204 || res.headers.get("content-length") === "0") return undefined as T;
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("json")) return (await res.json()) as T;
  const text = await res.text();
  return (text === "" ? undefined : text) as T;
}

/**
 * Untyped helper for endpoints not (yet) in the generated schema. Same cookies, CSRF header, problem parsing and session-expiry
 * handling as the typed client.
 */
export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>("GET", path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>("POST", path, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>("PUT", path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>("PATCH", path, body, options),
  delete: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>("DELETE", path, body, options),
};
